import React, { FC, Fragment, useMemo } from "react";
import { View } from "react-native";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { useScreenSize } from "@lookiero/sty-psp-ui";
import {
  NextBestActionDefinitionAction,
  NextBestActionLayout as NextBestActionLayoutEnum,
  NextBestActionType,
} from "../../../../../../projection/nextBestActionDefinition/nextBestActionDefinition";
import {
  isCardAction,
  nextBestActionDefinitionHasLayout,
} from "../../../../../../projection/nextBestActionDefinition/nextBestActionDefinition.typeguards";
import {
  NextBestActionCardOnActionTriggeredFunction,
  NextBestActionCardStatus,
} from "../../../../components/nextBestActionItem/NextBestActionItem";
import { NextBestAction } from "../../NextBestAction";
import { useNextBestAction } from "../../behaviours/useNextBestAction";
import { NextBestActionDefinitionActionWithStatus } from "../nextBestActionCards/NextBestActionCards";
import { NextBestActionCarousel } from "../nextBestActionCards/nextBestActionCarousel/NextBestActionCarousel";
import { NextBestActionGrid } from "../nextBestActionCards/nextBestActionGrid/NextBestActionGrid";
import { style as nextBestActionLayoutStyle } from "./NextBestActionLayout.style";

interface LayoutProps {
  readonly action: NextBestActionDefinitionAction<NextBestActionType.HOST_DEFAULT>;
  readonly basePath: string;
  readonly country: Country;
  readonly onActionTriggered?: NextBestActionCardOnActionTriggeredFunction;
  readonly segment: Segment;
}

const Grid: FC<LayoutProps> = ({ action, basePath, country, onActionTriggered, segment }) => {
  const { actionStatuses } = useNextBestAction();
  const screenSize = useScreenSize();

  const NextBestActionCards = useMemo(
    () => (screenSize === "L" ? NextBestActionGrid : NextBestActionCarousel),
    [screenSize],
  );

  const cards = useMemo(() => {
    const cardActions: NextBestActionDefinitionActionWithStatus[] = [];

    action.children.forEach((child) => {
      if (isCardAction(child)) {
        cardActions.push({
          ...child,
          status: actionStatuses[child.id] || NextBestActionCardStatus.IDLE,
        });
      }
    });

    return cardActions;
  }, [action.children, actionStatuses]);

  if (cards.length === 0) {
    return null;
  }

  return onActionTriggered ? (
    <NextBestActionCards actions={cards} basePath={basePath} country={country} segment={segment} />
  ) : null;
};

const Vertical: FC<LayoutProps> = ({ action, basePath, country, segment }) => {
  return (
    <View testID="next-best-action-layout-vertical">
      {action.children.map((childAction) => (
        <NextBestAction
          key={childAction.id}
          action={childAction}
          basePath={basePath}
          country={country}
          segment={segment}
        />
      ))}
    </View>
  );
};

const Horizontal: FC<LayoutProps> = ({ action, basePath, country, segment }) => {
  const style = useMemo(() => nextBestActionLayoutStyle(), []);

  return (
    <View style={style.horizontal} testID="next-best-action-layout-horizontal">
      {action.children.map((childAction) => (
        <View key={childAction.id} style={style.horizontalChild}>
          <NextBestAction action={childAction} basePath={basePath} country={country} segment={segment} />
        </View>
      ))}
    </View>
  );
};

const LayoutComponent: Record<NextBestActionLayoutEnum, FC<LayoutProps>> = {
  [NextBestActionLayoutEnum.GRID]: Grid,
  [NextBestActionLayoutEnum.VERTICAL_LIST]: Vertical,
  [NextBestActionLayoutEnum.HORIZONTAL_LIST]: Horizontal,
};

interface NextBestActionLayoutProps {
  readonly action: NextBestActionDefinitionAction & { readonly children: NextBestActionDefinitionAction[] };
  readonly basePath: string;
  readonly country: Country;
  readonly segment: Segment;
}

const NextBestActionLayout: FC<NextBestActionLayoutProps> = ({ action, basePath, country, segment }) => {
  const { onActionTriggered } = useNextBestAction();

  const hasLayout = nextBestActionDefinitionHasLayout(action);
  const Component = hasLayout ? LayoutComponent[action.metadata.layout] : Fragment;

  return hasLayout ? (
    <Component
      action={action}
      basePath={basePath}
      country={country}
      segment={segment}
      onActionTriggered={onActionTriggered}
    />
  ) : (
    <>
      {action.children.map((childAction: NextBestActionDefinitionAction) => (
        <NextBestAction
          key={childAction.id}
          action={childAction}
          basePath={basePath}
          country={country}
          segment={segment}
        />
      ))}
    </>
  );
};

export { NextBestActionLayout };
